// BookshelfSection.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Button,
  Badge,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import axiosInstance from "utils/axiosInstance";

import { Plus, List, ChevronDown } from 'lucide-react';

import './BookshelfSection.css';

import QuestionSetCard from './components/QuestionSetCard';
import StoreCard from './components/StoreCard';
import TopicCard from './components/TopicCard';

import { useSelector, useDispatch } from "react-redux";

import { uploadImage, fetchImage } from "utils/images/imageManager";

import placeholder_image from "assets/images/info/card-placeholder-medium.png";


const DROPDOWN_OPTIONS = [
  { key: "submittedQuestionSets", label: "已填寫的題組" },
  { key: "followedStores", label: "已追蹤的店家" },
  { key: "followedTopics", label: "已追蹤的主題" },
];

const BookshelfSection = ({ t }) => {

  const dispatch = useDispatch();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(DROPDOWN_OPTIONS[0]); // default: 已填寫的題組

  const [questionSets, setQuestionSets] = useState([]);
  const [stores, setStores] = useState([]);
  const [topics, setTopics] = useState([]);

  const [loading, setLoading] = useState(false);
  const [dataFetchError, setDataFetchError] = useState(null);

  const [imageMap, setImageMap] = useState(new Map());

  useEffect(()=>{
    console.log('imageMap:', imageMap);
  },[imageMap])

  const toggleDropdown = () => setDropdownOpen(prev => !prev);

  const fetchSubmittedQuestionSets = async () => {
    try {
      setLoading(true);
      setDataFetchError(null);

      const res = await axiosInstance.get("/knovia/myInfo/submittedQuestionSets");
      const questionSets = res.data || [];
      setQuestionSets(questionSets);

      console.log("Fetched submitted question sets:", questionSets);
      return questionSets;
    } catch (err) {
      console.error("Failed to fetch submitted question sets", err);
      setDataFetchError("無法載入資料");
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowedStores = async () => {
    setLoading(true);
    setDataFetchError(null);
    try {
      const res = await axiosInstance.get("/knovia/myInfo/followedStores");
      setStores(res.data || []);
      return res.data;
    } catch (err) {
      console.error("Fetch failed:", err);
      setDataFetchError("無法載入店家資料");
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowedTopics = async () => {
    setLoading(true);
    setDataFetchError(null);
    try {
      const res = await axiosInstance.get("/knovia/myInfo/followedTopics");
      setTopics(res.data || []);
      return res.data;
    } catch (err) {
      console.error("Fetch failed:", err);
      setDataFetchError("無法載入主題資料");
    } finally {
      setLoading(false);
    }
  };

  const BATCH_SIZE = 5;

  const createdObjectUrls = new Set(); // track urls
  const fetchImagesForItems = async (items, getImageFilename) => {
    let newMap = new Map(imageMap);

    const itemsToFetch = items.filter(item => {
      const filename = getImageFilename(item);
      return filename && !newMap.has(filename);
    });

    for (let i = 0; i < itemsToFetch.length; i += BATCH_SIZE) {
      const batch = itemsToFetch.slice(i, i + BATCH_SIZE);

      const results = await Promise.all(
        batch.map(async item => {
          const filename = getImageFilename(item);
          try {
            const { blob } = await fetchImage(filename, "original");
            const objectUrl = URL.createObjectURL(blob);
            createdObjectUrls.add(objectUrl); // track it
            return { filename, objectUrl };
          } catch (e) {
            console.error("Image load failed:", filename, e);
            return null;
          }
        })
      );

      for (const result of results) {
        console.log('result:', result);
        if (result) newMap.set(result.filename, result.objectUrl);
      }

      setImageMap(new Map(newMap));
    }
  };

  useEffect(() => {
    return () => {
      for (const url of createdObjectUrls) {
        URL.revokeObjectURL(url);
      }
      createdObjectUrls.clear(); // clean the tracker
    };
  }, []);

  useEffect(() => {
    const loadDataAndImages = async () => {
      switch (selectedOption.key) {
        case "submittedQuestionSets": {
          const sets = await fetchSubmittedQuestionSets();
          if (sets) await fetchImagesForItems(sets, item => item.image);
          break;
        }
        case "followedStores": {
          const stores = await fetchFollowedStores(); // make it return data
          if (stores) await fetchImagesForItems(stores, item => item.image);
          break;
        }
        case "followedTopics": {
          const topics = await fetchFollowedTopics(); // make it return data
          if (topics) await fetchImagesForItems(topics, item => item.image);
          break;
        }
        default:
          break;
      }
    };

    loadDataAndImages();
  }, [selectedOption]);


  //--------------------------------------------------
  // Return
  //--------------------------------------------------

  return (
    <div className="d-block">
      <Row style={{ marginBottom: "10px" }}>
        <Col className="info-profile-carousel-col">
          <div className="d-flex justify-content-between" style={{ paddingLeft: "35px" }}>
            <div className="d-flex align-items-center">
              <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle 
                  tag="div"
                  /*onClick={toggleDropdown}*/
                  data-toggle="dropdown"
                  aria-expanded={dropdownOpen}
                  className="bookshelf-custom-dropdown-toggle"
                >
                  <span className="dropdown-label">{selectedOption.label}</span>
                  <ChevronDown size={16} style={{ marginLeft: "6px" }} />
                </DropdownToggle>
                <DropdownMenu>
                  {DROPDOWN_OPTIONS.map(option => (
                    <DropdownItem
                      key={option.key}
                      onClick={() => setSelectedOption(option)}
                      active={option.key === selectedOption.key}
                      className="bookshelf-custom-dropdown-item"
                    >
                      {option.label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        </Col>
      </Row>

      <Container style={{ padding: "5px 20px" }}>
        {loading && <p>載入中...</p>}
        {dataFetchError && <p className="text-danger">{dataFetchError}</p>}

        {selectedOption.key === "submittedQuestionSets" &&
          (questionSets.length === 0 && !loading
            ? <p>目前沒有已填寫的題組</p>
            : questionSets.map((qs, idx) => (
                <QuestionSetCard 
                  key={idx} 
                  questionSet={qs} 
                  imageUrl={imageMap.get(qs?.image) || placeholder_image}
                />
              ))
          )}

        {selectedOption.key === "followedStores" &&
          (stores.length === 0 && !loading
            ? <p>目前沒有追蹤的店家</p>
            : stores.map((store, idx) => (
                <StoreCard 
                  key={idx} 
                  store={store} 
                  imageUrl={imageMap.get(store.image) || placeholder_image}
                />
              ))
          )}

        {selectedOption.key === "followedTopics" &&
          (topics.length === 0 && !loading
            ? <p>目前沒有追蹤的主題</p>
            : topics.map((topic, idx) => (
                <TopicCard 
                  key={idx} 
                  topic={topic} 
                  imageUrl={imageMap.get(topic.image) || placeholder_image}
                />
              ))
          )}
      </Container>
    </div>
  );
};

export default withTranslation()(BookshelfSection);


